import React, {useState, useEffect, useRef} from 'react';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import {User, LogOut} from 'lucide-react';
import styles from './magblocks-menu-bar.css';

const APP_URL = process.env.MAGNUM_APP_URL;
const API_URL = process.env.MAGNUM_API_URL;

function getInitials (user) {
    if (user.name) {
        const parts = user.name.trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : parts[0][0].toUpperCase();
    }
    if (user.email) return user.email[0].toUpperCase();
    return '?';
}

function getDisplayName (user) {
    return user.name || user.email || '';
}

const UserMenu = (props) => {
    const {user} = props;
    const [open, setOpen] = useState(false);
    const [signingOut, setSigningOut] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onMouseDown = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open]);

    const handleSignOut = async () => {
        setSigningOut(true);
        try {
            await fetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (_) {
            // ignore network errors — clear state anyway
        }
        props.onSignOut();
        toast.success('Signed out successfully');
    };

    const avatarSrc = user.profilePicture || null;
    const initials = getInitials(user);
    const displayName = getDisplayName(user);

    return (
        <div
            className={styles.userMenuContainer}
            ref={containerRef}
        >
            <button
                className={styles.avatarBtn}
                onClick={() => setOpen(o => !o)}
            >
                {avatarSrc ? (
                    <img
                        alt={displayName}
                        className={styles.avatarImg}
                        src={avatarSrc}
                    />
                ) : (
                    <span className={styles.avatarInitials}>{initials}</span>
                )}
            </button>

            {open && (
                <div className={styles.dropdown}>
                    {/* Header */}
                    <div className={styles.dropdownHeader}>
                        <span className={styles.dropdownName}>{displayName}</span>
                        <span className={styles.dropdownEmail}>{user.email}</span>
                    </div>

                    <div className={styles.dropdownSep} />

                    <a
                        className={styles.dropdownItem}
                        href={`${APP_URL}/en/profile`}
                        onClick={() => setOpen(false)}
                    >
                        <User
                            size={14}
                            strokeWidth={2}
                        />
                        {'My Profile'}
                    </a>

                    <div className={styles.dropdownSep} />

                    <button
                        className={styles.dropdownItem}
                        disabled={signingOut}
                        onClick={handleSignOut}
                    >
                        <LogOut
                            size={14}
                            strokeWidth={2}
                        />
                        {signingOut ? 'Signing out…' : 'Sign Out'}
                    </button>
                </div>
            )}
        </div>
    );
};

UserMenu.propTypes = {
    user: PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string,
        profilePicture: PropTypes.string
    }).isRequired,
    onSignOut: PropTypes.func.isRequired
};

export default UserMenu;
